import { pick } from "@iscr/helpers/json_functions";
import currency from "currency.js";
import sortBy from "lodash.sortby";
export class ProductPriceCalculator {
    constructor({ loyaltyEnabled, specPrices, priceCoeff, orgLoyaltyTierId, loyaltyTiers, }) {
        this.loyaltyEnabled = loyaltyEnabled;
        this.specPrices = specPrices;
        this.priceCoeff = priceCoeff;
        this.orgLoyaltyTierId = orgLoyaltyTierId;
        this.loyaltyTiers = loyaltyTiers;
    }
    findClosestVolumePrice(volumePrice, qty, price) {
        if (!volumePrice) {
            return price;
        }
        return Object.entries(volumePrice)
            .sort(([a], [b]) => Number(a) - Number(b))
            .reduce((acc, [key, val]) => {
            Number(key) <= qty && (acc = val);
            return acc;
        }, price);
    }
    getProductLoyaltyPrice(loyaltyPricing) {
        if (!this.loyaltyEnabled) {
            return null;
        }
        if (!this.orgLoyaltyTierId ||
            !this.loyaltyTiers.length ||
            !this.loyaltyTiers.find(t => t.tier_id === this.orgLoyaltyTierId)) {
            throw new Error(`Invalid orgLoyaltyTierId: ${this.orgLoyaltyTierId}`);
        }
        let lowestPrice = null;
        let lowestPriceTier = null;
        // Find the lowest price applicable to the user's loyalty tier, searching from the lowest tier upwards
        // until we reach the user's tier
        for (const tier of sortBy(this.loyaltyTiers, "threshold")) {
            const tier_price = loyaltyPricing ? loyaltyPricing[tier.tier_id] : null;
            if (tier_price && typeof tier_price === "number" && (lowestPrice === null || tier_price <= lowestPrice)) {
                lowestPrice = tier_price;
                lowestPriceTier = tier;
            }
            // we've reached the user's tier, stop searching
            if (this.orgLoyaltyTierId === tier.tier_id) {
                break;
            }
        }
        // return null if no price found
        if (lowestPrice === null) {
            return null;
        }
        if (!lowestPriceTier) {
            throw new Error("Lowest price tier should be defined if lowest price is defined");
        }
        return {
            price: lowestPrice,
            tier: pick(lowestPriceTier, ["tier_id", "name"]),
        };
    }
    calculateProductPrice({ pid, qty, price, volumePrice, loyaltyPricing }) {
        const closestVolumePrice = this.findClosestVolumePrice(volumePrice, qty, price);
        const loyaltyPrice = this.getProductLoyaltyPrice(loyaltyPricing);
        // Determine the best price between volume price and loyalty price
        let discountedPrice = closestVolumePrice;
        let loyaltyApplied = false;
        if (loyaltyPrice && loyaltyPrice.price < discountedPrice) {
            discountedPrice = loyaltyPrice.price;
            loyaltyApplied = true;
        }
        const getUnitPrice = (comparisonPrice) => this.specPrices[pid]
            ? currency(Math.min(comparisonPrice, this.specPrices[pid]))
            : currency(comparisonPrice).multiply(this.priceCoeff);
        // Determine final unit price after volume and loyalty discounts, and after spec price and price coeff
        const unitPrice = getUnitPrice(discountedPrice);
        // Determine unit price before volume and loyalty discounts, but after spec price and price coeff
        const originalUnitPrice = getUnitPrice(price);
        const total = unitPrice.multiply(qty);
        // Calculate original total for showing discounted amount in invoices and UI
        const originalTotal = originalUnitPrice.multiply(qty);
        return {
            total,
            unitPrice,
            appliedLoyaltyPrice: 
            // There may be a loyalty price that didn't end up being applied (e.g. volume price was better)
            // so check it was actually applied
            loyaltyPrice && loyaltyApplied
                ? {
                    tier: loyaltyPrice.tier,
                    originalTotal: originalTotal.value,
                    originalUnitPrice: originalUnitPrice.value,
                }
                : null,
        };
    }
    calculateCartSubtotals(productsInBasket, promoTotals) {
        const subtotals = productsInBasket.reduce((acc, prod) => ({
            originalProductTotal: acc.originalProductTotal.add(prod.appliedLoyaltyPrice && this.loyaltyEnabled
                ? prod.appliedLoyaltyPrice.originalTotal
                : prod.total),
            promotionDiscountTotal: acc.promotionDiscountTotal.add(prod.discountedTotal ? currency(prod.total).subtract(prod.discountedTotal) : 0),
            loyaltyDiscountTotal: acc.loyaltyDiscountTotal.add(prod.appliedLoyaltyPrice && this.loyaltyEnabled
                ? currency(prod.appliedLoyaltyPrice.originalTotal).subtract(prod.total)
                : 0),
        }), {
            originalProductTotal: currency(0),
            promotionDiscountTotal: currency(0),
            loyaltyDiscountTotal: currency(0),
        });
        const originalProductTotal = subtotals.originalProductTotal.add(promoTotals.originalTotal);
        const promotionDiscountTotal = subtotals.promotionDiscountTotal.add(promoTotals.totalDiscount);
        const loyaltyDiscountTotal = subtotals.loyaltyDiscountTotal;
        return {
            originalProductTotal,
            promotionDiscountTotal,
            loyaltyDiscountTotal,
            subtotalExclGstDeliveryAndDiscountCode: originalProductTotal
                .subtract(promotionDiscountTotal)
                .subtract(loyaltyDiscountTotal),
        };
    }
}
